import { Fragment, useState, useEffect, Dispatch, SetStateAction } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
	RectangleStackIcon,
	MapIcon,
	XMarkIcon,
	AtSymbolIcon,
	CalendarIcon,
	ServerIcon,
	ArrowLeftOnRectangleIcon,
	ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";
import Link from "next/link";
import Image from "next/image";
import CaryLogo from "public/logo.png";
import DataSources from "./dataSources";
import ContactUs from "./contactUs";
import useUser, { fetcher } from "functions/useUser";
import useSWR, { useSWRConfig } from "swr";
import { useRouter } from "next/router";

const navigation = [
	{ name: "World Map", href: "/", icon: MapIcon },
	{ name: "Database", href: "/data", icon: RectangleStackIcon },
];

export default function Sidebar({
	sidebarOpen,
	setSidebarOpen,
	page,
	breakpointLG,
	openContact,
	setOpenContact,
}: {
	sidebarOpen: boolean;
	setSidebarOpen: Dispatch<SetStateAction<boolean>>;
	page: string;
	breakpointLG?: boolean;
	openContact?: boolean;
	setOpenContact?: Dispatch<SetStateAction<boolean>>;
}) {
	const router = useRouter();
	const { mutate } = useSWRConfig();
	const [logout, setLogout] = useState(false);
	async function startLogout() {
		setLogout(true);
		await fetch("/api/auth/logout", { method: "post", credentials: "include" });
		mutate("/api/auth/@me");
		router.push("/");
	}

	const [sourcesOpen, setSourcesOpen] = useState(false);
	const [contactOpen, setContactOpen] = useState(false);
	const { isLoggedIn } = useUser();
	useEffect(() => {
		if (openContact) setContactOpen(true);
	}, [openContact]);
	useEffect(() => {
		if (!contactOpen && setOpenContact) setOpenContact(false);
	}, [contactOpen]);

	const { data: lastUpdated } = useSWR<{ error: boolean; data: string }>("/api/lastUpdated", fetcher);
	return (
		<>
			<Transition.Root show={sidebarOpen} as={Fragment}>
				<Dialog as="div" className={`relative z-40 ${breakpointLG ? "lg:hidden" : "md:hidden"}`} onClose={setSidebarOpen}>
					<Transition.Child
						as={Fragment}
						enter="transition-opacity ease-linear duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="transition-opacity ease-linear duration-300"
						leaveFrom="opacity-100"
						leaveTo="opacity-0">
						<div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
					</Transition.Child>

					<div className="fixed inset-0 z-40 flex">
						<Transition.Child
							as={Fragment}
							enter="transition ease-in-out duration-300 transform"
							enterFrom="-translate-x-full"
							enterTo="translate-x-0"
							leave="transition ease-in-out duration-300 transform"
							leaveFrom="translate-x-0"
							leaveTo="-translate-x-full">
							<Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white pt-5 pb-4">
								<Transition.Child
									as={Fragment}
									enter="ease-in-out duration-300"
									enterFrom="opacity-0"
									enterTo="opacity-100"
									leave="ease-in-out duration-300"
									leaveFrom="opacity-100"
									leaveTo="opacity-0">
									<div className="absolute top-0 right-0 -mr-12 pt-2">
										<button
											type="button"
											className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
											onClick={() => setSidebarOpen(false)}>
											<span className="sr-only">Close sidebar</span>
											<XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
										</button>
									</div>
								</Transition.Child>
								<div className="flex flex-shrink-0 items-center px-4">
									<Image className="h-8 w-auto" src={CaryLogo} alt="Cary Institute of Ecology Logo" />
								</div>
								<div className="mt-5 h-0 flex-1 overflow-y-auto">
									<nav className="space-y-1 px-2">
										{navigation.map((item) => (
											<Link href={item.href} key={`${item.name}-mobile`}>
												<a
													className={`${
														page === item.name && !contactOpen && !sourcesOpen
															? "bg-gray-100 text-gray-900"
															: "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
													} group flex items-center rounded-md px-2 py-2 text-base font-medium`}>
													<item.icon
														className={`${
															page === item.name ? "text-gray-500" : "text-gray-400 group-hover:text-gray-500"
														} mr-4 h-6 w-6 flex-shrink-0`}
														aria-hidden="true"
													/>
													{item.name}
												</a>
											</Link>
										))}
										<button
											onClick={() => {
												setContactOpen(true);
											}}
											key="date-mobile"
											className={`${
												contactOpen ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
											} group flex w-full items-center rounded-md px-2 py-2 text-base font-medium`}>
											<AtSymbolIcon
												className={`${contactOpen ? "text-gray-500" : "text-gray-400 group-hover:text-gray-500"} mr-4 h-6 w-6 flex-shrink-0`}
												aria-hidden="true"
											/>
											Contact Us
										</button>
										<button
											onClick={() => {
												setSourcesOpen(true);
											}}
											key="data-mobile"
											className={`${
												sourcesOpen ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
											} group flex w-full items-center rounded-md px-2 py-2 text-base font-medium`}>
											<CalendarIcon
												className={`${sourcesOpen ? "text-gray-500" : "text-gray-400 group-hover:text-gray-500"} mr-4 h-6 w-6 flex-shrink-0`}
												aria-hidden="true"
											/>
											Data Sources
										</button>
									</nav>
								</div>
							</Dialog.Panel>
						</Transition.Child>
						<div className="w-14 flex-shrink-0" aria-hidden="true">
							{/* Dummy element to force sidebar to shrink to fit close icon */}
						</div>
					</div>
				</Dialog>
			</Transition.Root>

			{/* Static sidebar for desktop */}
			<div
				className={`hidden ${
					breakpointLG ? "lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col" : "md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col"
				}`}>
				{/* Sidebar component, swap this element with another sidebar if you like */}
				<div className="flex flex-grow flex-col overflow-y-auto border-r border-gray-200 bg-white pt-5">
					<div className="flex flex-shrink-0 items-center px-4">
						<Image className="h-8 w-auto" src={CaryLogo} alt="Cary Institute of Ecology Logo" />
					</div>
					<div className="mt-5 flex flex-grow flex-col">
						<nav className="flex-1 space-y-1 px-2 pb-4">
							{navigation.map((item) => (
								<Link href={item.href} key={`${item.name}-desktop`}>
									<a
										className={`${
											page === item.name && !contactOpen && !sourcesOpen
												? "bg-gray-100 text-gray-900"
												: "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
										} group flex items-center rounded-md px-2 py-2 text-sm font-medium`}>
										<item.icon
											className={`${page === item.name ? "text-gray-500" : "text-gray-400 group-hover:text-gray-500"} mr-3 h-6 w-6 flex-shrink-0`}
											aria-hidden="true"
										/>
										{item.name}
									</a>
								</Link>
							))}
							<button
								onClick={() => {
									setContactOpen(true);
								}}
								key="date-desktop"
								className={`${
									contactOpen ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
								} group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium`}>
								<AtSymbolIcon
									className={`${contactOpen ? "text-gray-500" : "text-gray-400 group-hover:text-gray-500"} mr-3 h-6 w-6 flex-shrink-0`}
									aria-hidden="true"
								/>
								Contact Us
							</button>
							<button
								onClick={() => {
									setSourcesOpen(true);
								}}
								key="data-desktop"
								className={`${
									sourcesOpen ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
								} group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium`}>
								<CalendarIcon
									className={`${sourcesOpen ? "text-gray-500" : "text-gray-400 group-hover:text-gray-500"} mr-3 h-6 w-6 flex-shrink-0`}
									aria-hidden="true"
								/>
								Data Sources
							</button>
							{(page === "Admin" || isLoggedIn) && (
								<Link href="/admin" key={`admin-desktop`}>
									<a
										className={`${
											page === "Admin" && !contactOpen && !sourcesOpen
												? "bg-gray-100 text-gray-900"
												: "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
										} group flex items-center rounded-md px-2 py-2 text-sm font-medium`}>
										<ServerIcon
											className={`${page === "Admin" ? "text-gray-500" : "text-gray-400 group-hover:text-gray-500"} mr-3 h-6 w-6 flex-shrink-0`}
											aria-hidden="true"
										/>
										Admin
									</a>
								</Link>
							)}
							{(page === "Import" || isLoggedIn) && (
								<Link href="/admin/import" key={`import-desktop`}>
									<a
										className={`${
											page === "Import" && !contactOpen && !sourcesOpen
												? "bg-gray-100 text-gray-900"
												: "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
										} group flex items-center rounded-md px-2 py-2 text-sm font-medium`}>
										<ArrowUpTrayIcon
											className={`${page === "Import" ? "text-gray-500" : "text-gray-400 group-hover:text-gray-500"} mr-3 h-6 w-6 flex-shrink-0`}
											aria-hidden="true"
										/>
										Import
									</a>
								</Link>
							)}
							{isLoggedIn && (
								<button
									onClick={startLogout}
									className="group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900">
									<ArrowLeftOnRectangleIcon
										className={`${page === "Admin" ? "text-gray-500" : "text-gray-400 group-hover:text-gray-500"} mr-3 h-6 w-6 flex-shrink-0`}
										aria-hidden="true"
									/>
									{logout ? "Logging Out..." : "Logout"}
								</button>
							)}
						</nav>
						<div className="px-2 pb-4 text-center">
							<button
								onClick={() => {
									setSourcesOpen(true);
								}}
								className="w-full items-center rounded-md bg-gray-50 px-2 py-2 text-sm font-medium text-gray-600">
								Last Updated <strong>{lastUpdated?.data || "Loading"}</strong>
							</button>
						</div>
					</div>
				</div>
			</div>
			<DataSources
				open={sourcesOpen}
				onClose={() => {
					setSourcesOpen(false);
				}}
				page={page}
				lastUpdated={lastUpdated?.data || "Loading"}
			/>
			<ContactUs
				open={contactOpen}
				onClose={() => {
					setContactOpen(false);
				}}
			/>
		</>
	);
}
